import { formatPrice } from "@/utils/currency";
import { stopBellSound } from "@/lib/bellSound";
import { QuickStatusButton } from "@/components/orders/QuickStatusButton";
import { Bike, DollarSign, CreditCard, Timer, CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";

export function OrderCard({ order, deliverers, selectedOrderId, newOrderAlertId, groupByStatus, statusConfig, onSelect, onAdvance, dismissAlert }) {
  const { t } = useTranslation();
  const isSelected = selectedOrderId === order.id;
  const delivererName = deliverers.find(d => d.id === order.deliverer_id)?.name;
  const orderNum = order.order_number || order.id.substring(0, 6).toUpperCase();
  const timeStr = new Date(order.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  const isPickup = order.source === "pickup" || (!order.delivery_address && !order.table_number);
  const isTable = !!order.table_number;
  const typeLabel = isTable ? t("admin.table") : isPickup ? t("admin.pickup") : t("admin.delivery");
  const typeBg = isTable ? "bg-blue-100 text-blue-700" : isPickup ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  const isNewOrder = newOrderAlertId === order.id;
  const _sd = order.scheduled_for ? new Date(order.scheduled_for) : null;
  const isScheduled = !!_sd && !isNaN(_sd.getTime());
  const nowMs = Date.now();
  // For scheduled orders: measure urgency from the scheduled time, not from creation.
  // If order hasn't reached its scheduled time yet, elapsed = 0 (never urgent prematurely).
  const elapsedFromScheduled = isScheduled ? Math.floor((nowMs - new Date(order.scheduled_for).getTime()) / 60000) : 0;
  const elapsed = isScheduled
    ? Math.max(0, elapsedFromScheduled)
    : Math.floor((nowMs - new Date(order.created_at)) / 60000);
  const isUrgent = elapsed > 30 && !["delivered", "cancelled"].includes(order.status);
  const isWarning = elapsed > 15 && elapsed <= 30 && !["delivered", "cancelled"].includes(order.status);
  // Formatted scheduled time for display (HH:MM)
  const scheduledTimeStr = isScheduled
    ? new Date(order.scheduled_for).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    : null;
  const statusCfg = statusConfig[order.status] || statusConfig.pending;

  const paymentShort = (order.payment_method || "").toLowerCase();
  const payIcon = paymentShort.includes("visa") || paymentShort.includes("master") || paymentShort.includes("card") ? "card" :
                  paymentShort.includes("cash") || paymentShort.includes("efectivo") || paymentShort.includes("dinheiro") ? "cash" :
                  paymentShort.includes("online") || paymentShort.includes("stripe") ? "online" : "";

  const handleClick = () => {
    stopBellSound();
    dismissAlert();
    onSelect(order);
  };

  return (
    <div
      className={`cursor-pointer transition-all border-l-4 ${
        isNewOrder ? "bg-red-50 border-l-red-500 animate-pulse" :
        isSelected ? "bg-emerald-50 border-l-emerald-500" :
        isScheduled ? "bg-blue-50/30 border-l-blue-400" :
        isUrgent ? "bg-red-50/50 border-l-red-400" :
        isWarning ? "bg-amber-50/50 border-l-amber-400" :
        "border-l-transparent hover:bg-gray-50"
      }`}
      onClick={handleClick}
      data-testid={`order-item-${order.id.slice(-6)}`}
    >
      {/* Scheduled banner */}
      {isScheduled && (
        <div className="px-4 py-1 bg-blue-500 text-white flex items-center gap-1.5 text-[10px] font-bold">
          <CalendarClock className="w-3 h-3" />
          AGENDADO {scheduledTimeStr && `para as ${scheduledTimeStr}`}
          {elapsedFromScheduled < 0 && (
            <span className="ml-auto">{Math.abs(elapsedFromScheduled)}m restantes</span>
          )}
        </div>
      )}
      <div className="px-4 py-3">
      {/* Row 1: Customer name — primary element */}
      <div className="mb-1">
        <span className="block text-[20px] font-extrabold text-gray-900 uppercase leading-tight truncate">
          {order.customer_name || "Cliente"}
        </span>
      </div>

      {/* Row 2: Order # + Type + Price */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-black text-gray-900">#{orderNum}</span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${typeBg}`}>{typeLabel}</span>
          {isTable && <span className="text-[10px] font-bold text-blue-600">M{order.table_number}</span>}
          {order.source && order.source !== "pickup" && order.source !== "delivery" && order.source !== "table" && (
            <span className="text-[10px] text-gray-400 font-medium">{order.source}</span>
          )}
        </div>
        <span className="text-sm font-black text-gray-900">{formatPrice(order.total)}</span>
      </div>

      {/* Row 3: Status + Deliverer + Items | Time + Quick Action */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {!groupByStatus && (
            <span className={`text-[10px] font-bold ${statusCfg.color}`}>{statusCfg.label}</span>
          )}
          {delivererName && (
            <span className="text-[10px] text-purple-600 flex items-center gap-0.5">
              <Bike className="w-3 h-3" />{delivererName}
            </span>
          )}
          {order.items && (
            <span className="text-[10px] text-gray-400">{order.items.length} {order.items.length === 1 ? "item" : "itens"}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {payIcon === "cash" && <DollarSign className="w-3 h-3 text-green-500" />}
          {payIcon === "card" && <CreditCard className="w-3 h-3 text-blue-500" />}
          <span className="text-[10px] text-gray-400 font-medium">{timeStr}</span>
          {elapsed < 120 && !["delivered", "cancelled"].includes(order.status) && (
            <span className={`text-[10px] font-bold flex items-center gap-0.5 ${isUrgent ? "text-red-600" : isWarning ? "text-amber-600" : "text-gray-400"}`}>
              <Timer className="w-3 h-3" />{elapsed}m
            </span>
          )}
          <QuickStatusButton order={order} onAdvance={onAdvance} />
        </div>
      </div>
      </div>
    </div>
  );
}
